import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { ApiError, AuthenticationService, UserService } from '/src/api';
import { PasswordService } from '/src/api/services/password-service';
import { TUserId, User } from '/src/lib/models';
import { CreateUserForm, Nullable, TBaseQuery, TCreateUserRequest, UserRole, UserStatus } from '/src/lib/types';
import { getSessionUser, removeSessionUser, setSessionUser } from '/src/utils';

export interface UserStoreProps {
  /**
   * Logged-in User
   */
  _user?: User;
  /**
   * List of Users
   */
  users?: User[];
  /**
   * Currently selected User. This is NOT the logged-in user, but the User currently being viewed on a CRUD page
   */
  currentUser?: User;
  /**
   * Whether the User CRUD operation was successful
   */
  success: Nullable<boolean>;
  /**
   * Any errors that occurred during the CRUD operation
   */
  error: Nullable<string>;
  /**
   * Whether the CRUD data has been submitted
   */
  submitted: boolean;
}

export class UserStore extends BaseStore implements UserStoreProps {
  _user?: User; // logged-in User

  users?: User[];
  currentUser?: User; // User being viewed on CRUD pages

  success: Nullable<boolean> = null;
  error: Nullable<string> = null;
  submitted = false;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      _user: observable,
      users: observable,
      currentUser: observable,
      success: observable,
      error: observable,
      submitted: observable,
    });
  }

  get user(): User | undefined {
    if (!this._user) this.restoreFromStorage();

    return this._user;
  }

  get isAdmin() {
    return this.user && this.hasRole(UserRole.Admin);
  }

  get isLender() {
    return this.user && this.hasRole(UserRole.Investor);
  }

  get isCompany() {
    return this.user && this.hasRole(UserRole.Company);

    // NOTE: this is how it was originally:
    // return this.user && !this.isAdmin && !this.isLender;
  }

  hasRole = (role: UserRole) => (!this.user ? false : this.user.roles?.includes(role));

  restoreFromStorage = () => {
    const user = getSessionUser() as User;
    if (user) runInAction(() => (this._user = user));
  };

  /**** User CRUD ****/

  /**
   * Fetches a list of Users.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Users
   */
  query = async (query?: TBaseQuery) => {
    if (query) query.px = true;
    const response = await UserService.query(query);

    runInAction(() => (this.users = response));

    return this.users;
  };

  /**
   * Fetches a single User and sets it as the current User.
   *
   * @param userId - ID of the User to fetch
   * @returns A User object
   */
  get = async (userId: TUserId) => {
    const response = await UserService.get(userId);

    runInAction(() => (this.currentUser = response));

    return this.currentUser;
  };

  /**
   * Creates an User.
   *
   * @param formValues - A populated CreateUserForm object
   * @returns The new User
   */
  create = async (formValues: CreateUserForm) => {
    if (!this.user) throw new Error('Not authorized');

    // Create new User object
    const user: TCreateUserRequest = {
      username: formValues.username,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      displayName: formValues.firstName + ' ' + formValues.lastName,
      email: formValues.email,
      password: formValues.password,
      roles: [formValues.role],
      company: formValues.company,
      status: UserStatus.Active,
    };

    const response = await UserService.create(user);

    return response;
  };

  /**
   * Updates an User.
   *
   * @param user - The User to update
   * @returns The updated User
   */
  update = async (user: User) => {
    if (user.roles.includes(UserRole.Admin)) {
      user.company = null;
    } else {
      if (user.company && typeof user.company !== 'string') user.company = user?.company?._id;
    }

    const response = await UserService.update(user._id, user);

    runInAction(() => (this.currentUser = Object.assign(this.currentUser ?? {}, response)));

    return this.currentUser;
  };

  /**
   * Removes a User.
   *
   * @param userId - ID of the User to remove
   * @returns The remove User
   */
  remove = async (userId: TUserId) => {
    return await UserService.remove(userId);
  };

  /**** Authentication Controller ****/

  /**
   * Attempts to sign in using the given credentials.
   *
   * @param username - Username to log into
   * @param password - User password
   */
  signin = async (username: string, password: string) => {
    const response = await AuthenticationService.signin({ username, password });

    runInAction(() => (this._user = response));
    setSessionUser(response);
  };

  signout = async () => {
    try {
      await AuthenticationService.signout();

      runInAction(() => (this._user = undefined));
      removeSessionUser();
    } catch (err) {
      if ((err as ApiError).status === 401) {
        runInAction(() => (this._user = undefined));
        removeSessionUser();
      }
    }
  };

  /**** Settings Controller ****/

  /**
   * Lets non-Admin Users update their own data.
   *
   * @param user - The User to update
   * @param isValid - Whether the data has been validated
   * @returns The updated User
   */
  updateUserProfile = async (user: User, isValid: boolean) => {
    if (isValid) {
      runInAction(() => (this.success = this.error = null));

      try {
        const response = await UserService.update(user._id, user);

        runInAction(() => (this.success = true));

        return response;
      } catch (response) {
        runInAction(() => (this.error = (response as any).data.message));
      }
    } else {
      runInAction(() => (this.submitted = true));
    }
  };

  /**
   * Changes a User's password. Only the User whose password is being changed may perform this action.
   *
   * @param currentPassword - User's current password
   * @param newPassword - The new password
   * @param verifyPassword - Should match `newPassword`
   * @returns The update password response
   */
  changeUserPassword = async (currentPassword: string, newPassword: string, verifyPassword: string) => {
    runInAction(() => (this.success = this.error = null));

    try {
      const response = await PasswordService.updatePassword({ currentPassword, newPassword, verifyPassword });

      runInAction(() => (this.success = true));

      return response;
    } catch (response) {
      runInAction(() => (this.error = (response as Error).message));
    }
  };

  /**** Passwords Controller ****/

  /**
   * Request a password reset token be generated and sent to the given User's email address.
   *
   * @param username - User to start password reset process for
   */
  askForPasswordReset = async (username: string) => {
    return await PasswordService.forgotPassword({ username });
  };

  /**
   * Changes the password of a user who requested a password reset.
   *
   * @param token - Password reset token
   * @param newPassword - The new password
   * @param verifyPassword - Should match `newPassword`
   */
  resetUserPassword = async (token: string, newPassword: string, verifyPassword: string) => {
    const response = await PasswordService.resetPassword(token, { newPassword, verifyPassword });

    runInAction(() => (this._user = response));

    return response;
  };
}
